import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApplicationByToken, updateApplication, submitApplication } from '../../../services/crmApi';
import { uploadFile } from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PhoneInput from '../../Common/PhoneInput';
import toast from 'react-hot-toast';
import { Combobox } from '@headlessui/react';
import { COUNTRIES } from '../../../utils/countries';
import { NATIONALITIES } from '../../../utils/nationalities';
import { validateStoredPhoneValue } from '../../../utils/phoneUtils';
import { parseNIC } from '../../../utils/nicUtils';
import {
  getMaxApplicantDateOfBirth,
  getApplicantDateOfBirthError,
  isApplicantDateOfBirthAllowed,
} from '../../../utils/applicantDateOfBirth';

/**
 * PublicApplicationForm Component
 * Public form for students to complete their application and submit qualifications
 */
const PublicApplicationForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [countryQuery, setCountryQuery] = useState('');
  const [nationalityQuery, setNationalityQuery] = useState('');
  const totalSteps = 4;
  const maxApplicantDateOfBirth = useMemo(() => getMaxApplicantDateOfBirth(new Date()), []);

  // Form data
  const [formData, setFormData] = useState({
    attendanceMode: 'Physical',
    personalInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      nationality: '',
      idNumber: '',
      passportNumber: ''
    },
    contactInfo: {
      email: '',
      phone: '',
      alternatePhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: 'Sri Lanka',
        postalCode: ''
      }
    },
    permanentAddress: {
      street: '',
      city: '',
      state: '',
      country: 'Sri Lanka',
      postalCode: '',
      sameAsContact: false
    },
    guardianInfo: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    educationBackground: [],
    workExperience: [],
    documents: [],
    specialRequirements: '',
    additionalInfo: ''
  });

  useEffect(() => {
    fetchApplication();
  }, [token]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await getApplicationByToken(token);
      
      if (response.data.success) {
        const app = response.data.data;
        setApplication(app);
        setLoadError('');
        
        // Pre-fill form with existing data
          setFormData(prev => {
            const mergedContactInfo = {
              ...prev.contactInfo,
              ...app.contactInfo,
              address: {
                ...prev.contactInfo.address,
                ...(app.contactInfo?.address || {}),
              }
            };
            if (!mergedContactInfo.address.country || !String(mergedContactInfo.address.country).trim()) {
              mergedContactInfo.address.country = 'Sri Lanka';
            }

            const mergedPermanentAddress = {
              ...prev.permanentAddress,
              ...app.permanentAddress,
            };
            if (!mergedPermanentAddress.country || !String(mergedPermanentAddress.country).trim()) {
              mergedPermanentAddress.country = 'Sri Lanka';
            }

            return {
              ...prev,
              attendanceMode: app.attendanceMode || prev.attendanceMode || 'Physical',
              personalInfo: { ...prev.personalInfo, ...app.personalInfo },
              contactInfo: mergedContactInfo,
              permanentAddress: mergedPermanentAddress,
              guardianInfo: { ...prev.guardianInfo, ...app.guardianInfo },
              emergencyContact: { ...prev.emergencyContact, ...app.emergencyContact },
              educationBackground: app.educationBackground || [],
              workExperience: app.workExperience || [],
              documents: app.documents || [],
              specialRequirements: app.specialRequirements || '',
              additionalInfo: app.additionalInfo || ''
            };
          });
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      const message = error?.response?.data?.message || 'Failed to load application. Please check the link.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => {
      const next = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      };

      // Auto-extract DOB and Gender from Sri Lankan NIC
      if (section === 'personalInfo' && field === 'idNumber') {
        const parsed = parseNIC(value);
        if (parsed) {
          next.personalInfo.dateOfBirth = parsed.dob || next.personalInfo.dateOfBirth;
          next.personalInfo.gender = parsed.gender?.toLowerCase() || next.personalInfo.gender;
        }
      }
      return next;
    });
  };

  const handleNestedInputChange = (section, nested, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nested]: {
          ...prev[section][nested],
          [field]: value
        }
      }
    }));
  };

  const handleArrayAdd = (section, newItem) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  const handleArrayRemove = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = async (file, documentType) => {
    try {
      const metadata = {
        category: 'Qualification Documents',
        entityType: 'Application',
        entityId: application._id,
        description: documentType,
        title: `${documentType} - ${file.name}`
      };

      const response = await uploadFile(file, metadata);
      
      if (response.data.success) {
        const newDocument = {
          fileName: file.name,
          fileId: response.data.data._id,
          documentType,
          uploadDate: new Date().toISOString(),
          status: 'Uploaded'
        };

        handleArrayAdd('documents', newDocument);
        toast.success(`${documentType} uploaded successfully`);
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(`Failed to upload ${documentType}`);
    }
  };

  const buildNormalizedApplicationData = () => {
    if (
      formData.personalInfo.dateOfBirth &&
      !isApplicantDateOfBirthAllowed(formData.personalInfo.dateOfBirth, maxApplicantDateOfBirth)
    ) {
      throw new Error(getApplicantDateOfBirthError(maxApplicantDateOfBirth));
    }

    const primaryPhone = validateStoredPhoneValue(formData.contactInfo.phone, 'LK', { required: true });
    const alternatePhone = validateStoredPhoneValue(formData.contactInfo.alternatePhone, 'LK');
    const guardianPhone = validateStoredPhoneValue(formData.guardianInfo.phone, 'LK');
    const emergencyPhone = validateStoredPhoneValue(formData.emergencyContact.phone, 'LK');

    if (!primaryPhone.isValid) {
      throw new Error('Please enter a valid primary phone number');
    }

    if (formData.contactInfo.alternatePhone?.trim() && !alternatePhone.isValid) {
      throw new Error('Please enter a valid alternate phone number');
    }

    if (formData.guardianInfo.phone?.trim() && !guardianPhone.isValid) {
      throw new Error('Please enter a valid guardian phone number');
    }

    if (formData.emergencyContact.phone?.trim() && !emergencyPhone.isValid) {
      throw new Error('Please enter a valid emergency contact phone number');
    }

    return {
      ...formData,
      contactInfo: {
        ...formData.contactInfo,
        phone: primaryPhone.normalizedValue,
        alternatePhone: alternatePhone.isValid ? alternatePhone.normalizedValue : ''
      },
      guardianInfo: {
        ...formData.guardianInfo,
        phone: guardianPhone.isValid ? guardianPhone.normalizedValue : ''
      },
      emergencyContact: {
        ...formData.emergencyContact,
        phone: emergencyPhone.isValid ? emergencyPhone.normalizedValue : ''
      }
    };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateApplication(application._id, buildNormalizedApplicationData());
      toast.success('Application saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save application');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Save current data first
      await updateApplication(application._id, buildNormalizedApplicationData());
      
      // Then submit the application
      await submitApplication(application._id);
      
      toast.success('Application submitted successfully!');
      setApplication(prev => ({ ...prev, status: 'Submitted' }));
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const renderPersonalInfoStep = () => (
    <div className="space-y-6">
      {/* Mode of Attendance Selection Box */}
      <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-4 sm:p-5">
        <label className="block text-sm font-semibold text-gray-800 mb-1">
          Mode of Attendance <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-600 mb-3">
          Please select your preferred mode of attending lectures and course activities.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
            formData.attendanceMode === 'Physical'
              ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="attendanceMode"
              value="Physical"
              checked={formData.attendanceMode === 'Physical'}
              onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <div className="ml-3">
              <span className="block text-sm font-medium text-gray-900">Physical (On-Campus)</span>
              <span className="block text-xs text-gray-500">Attend classes in person at the campus center</span>
            </div>
          </label>
          <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
            formData.attendanceMode === 'Online'
              ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="attendanceMode"
              value="Online"
              checked={formData.attendanceMode === 'Online'}
              onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <div className="ml-3">
              <span className="block text-sm font-medium text-gray-900">Online (Virtual)</span>
              <span className="block text-xs text-gray-500">Participate live online via digital classrooms</span>
            </div>
          </label>
          <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
            formData.attendanceMode === 'Blended'
              ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="attendanceMode"
              value="Blended"
              checked={formData.attendanceMode === 'Blended'}
              onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <div className="ml-3">
              <span className="block text-sm font-medium text-gray-900">Blended Learning (Online + Physical)</span>
              <span className="block text-xs text-gray-500">Combine online sessions with in-person classes at the campus center</span>
            </div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
          <input
            type="text"
            value={formData.personalInfo.firstName}
            onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
          <input
            type="text"
            value={formData.personalInfo.lastName}
            onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
          <input
            type="date"
            value={formData.personalInfo.dateOfBirth}
            onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
            max={maxApplicantDateOfBirth}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Applicants must be at least 15 years old. The latest selectable date is {maxApplicantDateOfBirth}.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
          <select
            value={formData.personalInfo.gender}
            onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nationality *</label>
          {(() => {
            const currentNationality = formData.personalInfo.nationality || '';
            const filteredNationalities = nationalityQuery.trim()
              ? NATIONALITIES.filter((n) =>
                  n.toLowerCase().includes(nationalityQuery.trim().toLowerCase())
                )
              : NATIONALITIES;

            return (
              <Combobox
                immediate
                value={currentNationality}
                onChange={(value) => {
                  handleInputChange('personalInfo', 'nationality', value || '');
                  setNationalityQuery('');
                }}
              >
                <div className="relative">
                  <Combobox.Input
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    displayValue={(value) => value}
                    onChange={(e) => {
                      const next = e.target.value;
                      setNationalityQuery(next);
                      handleInputChange('personalInfo', 'nationality', next);
                    }}
                    placeholder="Search nationality"
                    required
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700">
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                    </svg>
                  </Combobox.Button>
                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                    {filteredNationalities.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500">No nationalities found</div>
                    ) : (
                      filteredNationalities.map((nationality) => (
                        <Combobox.Option
                          key={nationality}
                          value={nationality}
                          className={({ active }) =>
                            `cursor-pointer select-none px-3 py-2 ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'}`
                          }
                        >
                          {nationality}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </div>
              </Combobox>
            );
          })()}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ID Number</label>
          <input
            type="text"
            value={formData.personalInfo.idNumber}
            onChange={(e) => handleInputChange('personalInfo', 'idNumber', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );

  const renderContactInfoStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
          <input
            type="email"
            value={formData.contactInfo.email}
            onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <PhoneInput
          label="Phone Number *"
          name="contactInfo.phone"
          value={formData.contactInfo.phone}
          onChange={(e) => handleInputChange('contactInfo', 'phone', e.target.value)}
          placeholder="Enter phone number"
          required
        />
      </div>

      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Current Address</h4>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Street Address"
            value={formData.contactInfo.address.street}
            onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'street', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="City"
              value={formData.contactInfo.address.city}
              onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="text"
              placeholder="State/Province"
              value={formData.contactInfo.address.state}
              onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(() => {
              const currentCountry = formData.contactInfo.address.country || '';
              const filteredCountries = countryQuery.trim()
                ? COUNTRIES.filter((c) => c.toLowerCase().includes(countryQuery.trim().toLowerCase()))
                : COUNTRIES;

              return (
                <Combobox
                  immediate
                  value={currentCountry}
                  onChange={(value) => {
                    handleNestedInputChange('contactInfo', 'address', 'country', value || '');
                    setCountryQuery('');
                  }}
                >
                  <div className="relative">
                    <Combobox.Input
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Country"
                      displayValue={(value) => value}
                      onChange={(e) => {
                        const next = e.target.value;
                        setCountryQuery(next);
                        handleNestedInputChange('contactInfo', 'address', 'country', next);
                      }}
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700">
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                      </svg>
                    </Combobox.Button>
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                      {filteredCountries.length === 0 ? (
                        <div className="px-3 py-2 text-gray-500">No countries found</div>
                      ) : (
                        filteredCountries.map((country) => (
                          <Combobox.Option
                            key={country}
                            value={country}
                            className={({ active }) =>
                              `cursor-pointer select-none px-3 py-2 ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'}`
                            }
                          >
                            {country}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              );
            })()}
            <input
              type="text"
              placeholder="Postal Code"
              value={formData.contactInfo.address.postalCode}
              onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'postalCode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEducationStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">Educational Background</h4>
        <button
          type="button"
          onClick={() => handleArrayAdd('educationBackground', {
            institution: '',
            qualification: '',
            fieldOfStudy: '',
            startDate: '',
            endDate: '',
            grade: ''
          })}
          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-indigo-600 bg-indigo-100 hover:bg-indigo-200"
        >
          Add Education
        </button>
      </div>

      {formData.educationBackground.map((edu, index) => (
        <div key={index} className="border border-gray-200 rounded-md p-4">
          <div className="flex justify-between items-center mb-4">
            <h5 className="text-md font-medium text-gray-800">Education #{index + 1}</h5>
            <button
              type="button"
              onClick={() => handleArrayRemove('educationBackground', index)}
              className="text-red-600 hover:text-red-800"
            >
              Remove
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Institution Name"
              value={edu.institution}
              onChange={(e) => {
                const updated = [...formData.educationBackground];
                updated[index].institution = e.target.value;
                setFormData(prev => ({ ...prev, educationBackground: updated }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="text"
              placeholder="Qualification"
              value={edu.qualification}
              onChange={(e) => {
                const updated = [...formData.educationBackground];
                updated[index].qualification = e.target.value;
                setFormData(prev => ({ ...prev, educationBackground: updated }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderDocumentsStep = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-medium text-gray-900">Upload Documents</h4>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['Academic Transcripts', 'Certificates', 'ID Copy', 'CV/Resume'].map((docType) => (
          <div key={docType} className="border border-gray-200 rounded-md p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">{docType}</h5>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleFileUpload(file, docType);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        ))}
      </div>

      <div>
        <h5 className="text-sm font-medium text-gray-700 mb-4">Uploaded Documents</h5>
        {formData.documents.length === 0 ? (
          <p className="text-gray-500 text-sm">No documents uploaded yet</p>
        ) : (
          <div className="space-y-2">
            {formData.documents.map((doc, index) => (
              <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
                <span className="text-sm text-gray-700">{doc.fileName} ({doc.documentType})</span>
                <span className="text-xs text-green-600">{doc.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Unavailable</h1>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Not Found</h1>
          <p className="text-gray-600">The application link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (application.status === 'Submitted') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted</h1>
          <p className="text-gray-600 mb-4">Your application has been submitted successfully and is under review.</p>
          <p className="text-sm text-gray-500">Application Number: {application.applicationNumber}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-indigo-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">Complete Your Application</h1>
            <p className="text-indigo-100 mt-1">
              {application.subProductId?.name} - {application.intakeId?.code}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Step {currentStep} of {totalSteps}</span>
              <span className="text-sm text-gray-500">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Form Content */}
          <div className="px-6 py-8">
            {currentStep === 1 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
                {renderPersonalInfoStep()}
              </div>
            )}
            
            {currentStep === 2 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h2>
                {renderContactInfoStep()}
              </div>
            )}
            
            {currentStep === 3 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Educational Background</h2>
                {renderEducationStep()}
              </div>
            )}
            
            {currentStep === 4 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Documents & Qualifications</h2>
                {renderDocumentsStep()}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              {currentStep < totalSteps ? (
                <button
                  onClick={() => {
                    handleSave();
                    setCurrentStep(currentStep + 1);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicApplicationForm;